"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.APosPaymentGatewayConfigCreateInputSchema = exports.APosPaymentGatewayConfigSchema = exports.APosTenantSurchargeConfigSchema = exports.APosTenantTipConfigSchema = exports.APosSurchargeAgreementSchema = exports.APosSurchargeAgreementStatusSchema = exports.APosPaymentGatewaySchema = void 0;
const zod_1 = require("zod");
const timestamps_schema_1 = require("../../common/timestamps.schema");
const id_schema_1 = require("../../common/id.schema");
exports.APosPaymentGatewaySchema = zod_1.z.enum([
    'stripe', 'square', 'clover', 'pax', 'dejavoo',
    'nmi', 'external',
]);
// --- Surcharge Agreements (uploaded by SA) ---
exports.APosSurchargeAgreementStatusSchema = zod_1.z.enum(['active', 'archived']);
exports.APosSurchargeAgreementSchema = zod_1.z.object({
    id: zod_1.z.string(),
    // SA-set agreement label (e.g. "Visa Surcharge Disclosure"). Distinct from
    // fileName so the dropdown shows a clean label even if the PDF was renamed.
    agreementName: zod_1.z.string().min(1).max(120),
    // The negotiated maximum surcharge under this specific agreement. Source of
    // truth — when SA picks an agreement active, the gateway's runtime ceilings
    // (maxSurchargeRate / maxSurchargeFlat) are seeded from these fields.
    maxRate: zod_1.z.number().min(0).max(100),
    maxFlat: zod_1.z.number().nonnegative(),
    fileName: zod_1.z.string(),
    s3Key: zod_1.z.string(),
    contentType: zod_1.z.string().default('application/pdf'),
    sizeBytes: zod_1.z.number().nonnegative().default(0),
    uploadedAt: zod_1.z.number(),
    uploadedBy: zod_1.z.string().optional(),
    effectiveDate: zod_1.z.string().optional(),
    version: zod_1.z.string().optional(),
    notes: zod_1.z.string().max(500).optional(),
    status: exports.APosSurchargeAgreementStatusSchema.default('archived'),
});
// --- Tenant-owned tip config (lives on the location config) ---
exports.APosTenantTipConfigSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().default(false),
    percentages: zod_1.z.array(zod_1.z.number().min(0).max(100)).max(10).default([15, 18, 20]),
    allowCustom: zod_1.z.boolean().default(true),
    allowSkip: zod_1.z.boolean().default(true),
    /**
     * Merchant switch for tips on a CASH sale (needs the SA grant
     * `cashTipsEnabled`, independently of `tipsEnabled`). OPTIONAL WITH NO
     * DEFAULT: unset means "same as `enabled`", which is what every location
     * written before this field did.
     */
    cashEnabled: zod_1.z.boolean().optional(),
});
// --- Tenant-owned surcharge config (overrides bound to SA ceilings) ---
exports.APosTenantSurchargeConfigSchema = zod_1.z.object({
    apply: zod_1.z.boolean().default(false),
    /*
      Meaning depends on `overridesExplicit` (see effectiveSurchargeRates in
      surchargeRates.ts):
        absent/false (LEGACY) — 0 / null / absent = use the SA max.
        true (EXPLICIT)       — a number, incl. 0, is literal; null/absent = max.
      Always clamped to maxSurchargeRate / maxSurchargeFlat when read.
    */
    rateOverride: zod_1.z.number().min(0).max(100).nullable().default(0),
    flatOverride: zod_1.z.number().nonnegative().nullable().default(0),
    /** true once any save has touched rate/flat since 2026-10-07. No default: absent = legacy. */
    overridesExplicit: zod_1.z.boolean().optional(),
});
exports.APosPaymentGatewayConfigSchema = zod_1.z.object({
    // Composite key
    tenantId: id_schema_1.TenantIdSchema,
    locationId: id_schema_1.EntityIdSchema.optional(),
    configId: id_schema_1.EntityIdSchema,
    // Gateway identity
    gateway: exports.APosPaymentGatewaySchema,
    provider: exports.APosPaymentGatewaySchema.optional(),
    environment: zod_1.z.enum(['sandbox', 'production']).optional(),
    // SA-owned credentials
    merchantId: zod_1.z.string().optional(),
    apiKeyEncrypted: zod_1.z.string().optional(),
    apiSecretEncrypted: zod_1.z.string().optional(),
    securityKeyEncrypted: zod_1.z.string().optional(),
    securityKeyEncryptedAt: zod_1.z.number().nullable().optional(),
    webhookSecret: zod_1.z.string().optional(),
    sandboxMode: zod_1.z.boolean().default(false),
    // SA-owned capability flags + ceilings
    tipsEnabled: zod_1.z.boolean().default(false),
    /**
     * TIPS ON A CASH SALE — a separate grant from `tipsEnabled`.
     *
     * `tipsEnabled` grants a tip prompt on the CARD READER, and is meaningless
     * without one: a location with no device-backed tender has nothing to
     * prompt on. A cash tip is asked on our own customer display and needs no
     * reader at all, so it is a location that may have one and not the other.
     *
     * The PERCENTAGES are not duplicated — both use `tenantTipConfig`, so a
     * merchant sets their tip amounts once and they apply wherever tipping is
     * granted. This flag only answers "may cash sales ask".
     *
     * Default false: additive, and no location asks for a cash tip until it is
     * granted here and the merchant has switched their own tipping on.
     */
    cashTipsEnabled: zod_1.z.boolean().default(false),
    surchargeEnabled: zod_1.z.boolean().default(false),
    // Maximum allowed surcharge — tenant overrides cannot exceed these
    maxSurchargeRate: zod_1.z.number().min(0).max(100).default(0),
    maxSurchargeFlat: zod_1.z.number().nonnegative().default(0),
    // SA-owned agreements (PDFs) + which one is currently active
    surchargeAgreements: zod_1.z.array(exports.APosSurchargeAgreementSchema).default([]),
    activeSurchargeAgreementId: zod_1.z.string().nullable().optional(),
    // Tenant-owned settings (location-wide; broadcast to all terminals via WS)
    tenantTipConfig: exports.APosTenantTipConfigSchema.optional(),
    tenantSurchargeConfig: exports.APosTenantSurchargeConfigSchema.optional(),
    // Reject card transactions below this amount. 0 = no minimum.
    minCardAmount: zod_1.z.number().nonnegative().default(0),
    // Bookkeeping
    isDefault: zod_1.z.boolean().default(false),
    isActive: zod_1.z.boolean().default(true),
    status: zod_1.z.enum(['active', 'inactive']).default('active'),
    supportedMethods: zod_1.z.array(zod_1.z.enum(['card', 'contactless', 'apple_pay', 'google_pay', 'cash'])).default(['card']),
    processingFeePercent: zod_1.z.number().min(0).max(100).optional(),
    flatFee: zod_1.z.number().nonnegative().optional(),
    settlementSchedule: zod_1.z.enum(['daily', 'weekly', 'monthly']).optional(),
    lastTestedAt: zod_1.z.number().optional(),
    notes: zod_1.z.string().max(2000).optional(),
    // Bumps on every PUT (SA or tenant). POS uses this for WS-sync drift detection.
    version: zod_1.z.number().nonnegative().default(0),
}).merge(timestamps_schema_1.TimestampsSchema);
exports.APosPaymentGatewayConfigCreateInputSchema = zod_1.z.object({
    gateway: exports.APosPaymentGatewaySchema,
    locationId: id_schema_1.EntityIdSchema.optional(),
    isDefault: zod_1.z.boolean().optional(),
    merchantId: zod_1.z.string().optional(),
    apiKeyEncrypted: zod_1.z.string().optional(),
    apiSecretEncrypted: zod_1.z.string().optional(),
    webhookSecret: zod_1.z.string().optional(),
    sandboxMode: zod_1.z.boolean().optional(),
    supportedMethods: zod_1.z.array(zod_1.z.enum(['card', 'contactless', 'apple_pay', 'google_pay', 'cash'])).optional(),
    processingFeePercent: zod_1.z.number().min(0).max(100).optional(),
    flatFee: zod_1.z.number().nonnegative().optional(),
    notes: zod_1.z.string().max(2000).optional(),
});
//# sourceMappingURL=payment-gateway-config.schema.js.map